import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Server,
  Shield,
  Settings,
  AlertTriangle,
  CheckCircle,
  Info,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Download,
} from 'lucide-react';
import api from '../services/api';
import { frontendBuildInfo, formatBuildTimestamp } from '../buildInfo';

function SystemInfoPanel() {
  const [systemInfo, setSystemInfo] = useState(null);
  const [securityCheck, setSecurityCheck] = useState(null);
  const [environment, setEnvironment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Get initial tab from URL or default to 'system'
    return searchParams.get('systemTab') || 'system';
  });
  const [showSensitive, setShowSensitive] = useState(false);

  // Handle tab changes and update URL
  const handleTabChange = tab => {
    setActiveTab(tab);
    // Update URL search params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('systemTab', tab);
    setSearchParams(newSearchParams);
  };

  // Sync URL changes with active tab state
  useEffect(() => {
    const tabFromUrl = searchParams.get('systemTab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  const loadSystemInfo = async () => {
    setLoading(true);
    setError('');

    try {
      const [systemResponse, securityResponse, envResponse] = await Promise.all([
        api.get('/admin/system/system-info'),
        api.get('/admin/system/security-check'),
        api.get('/admin/system/environment'),
      ]);

      setSystemInfo(systemResponse);
      setSecurityCheck(securityResponse);
      setEnvironment(envResponse);
    } catch (err) {
      console.error('Error loading system info:', err);
      setError('Failed to load system information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSystemInfo();
  }, []);

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const downloadSystemInfo = () => {
    const data = {
      timestamp: new Date().toISOString(),
      systemInfo,
      securityCheck,
      environment,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-info-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'good':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'good':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const renderValue = value => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'None';
      }

      // Handle nested objects
      return Object.entries(value)
        .map(
          ([k, v]) =>
            `${k.replace(/([A-Z])/g, ' $1').trim()}: ${typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v}`,
        )
        .join(', ');
    }

    return String(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600">Loading system information...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
        <button onClick={loadSystemInfo} className="mt-2 btn btn-secondary btn-small">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">System Information & Security</h2>
        <div className="flex space-x-2">
          <button
            onClick={loadSystemInfo}
            className="btn btn-secondary btn-small flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={downloadSystemInfo}
            className="btn btn-secondary btn-small flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('system')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'system'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Server className="w-4 h-4 inline mr-2" />
            System Info
          </button>
          <button
            onClick={() => handleTabChange('security')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'security'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Security Check
          </button>
          <button
            onClick={() => handleTabChange('environment')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'environment'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Environment
          </button>
        </nav>
      </div>

      {/* System Info Tab */}
      {activeTab === 'system' && systemInfo && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Build &amp; Deploy</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Backend
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Version</span>
                    <span className="font-mono">{systemInfo.build?.appVersion || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Image tag</span>
                    <span className="font-mono">{systemInfo.build?.imageTag || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Git commit</span>
                    <span className="font-mono">{systemInfo.build?.buildShaShort || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Branch / ref</span>
                    <span className="font-mono">{systemInfo.build?.buildRef || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Built at</span>
                    <span className="font-mono text-right">
                      {formatBuildTimestamp(systemInfo.build?.buildTime)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Container started</span>
                    <span className="font-mono text-right">
                      {formatBuildTimestamp(systemInfo.build?.containerStartedAt)}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Frontend
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Version</span>
                    <span className="font-mono">{frontendBuildInfo.appVersion}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Image tag</span>
                    <span className="font-mono">{frontendBuildInfo.imageTag}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Git commit</span>
                    <span className="font-mono">{frontendBuildInfo.buildShaShort}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Branch / ref</span>
                    <span className="font-mono">{frontendBuildInfo.buildRef}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600">Built at</span>
                    <span className="font-mono text-right">
                      {formatBuildTimestamp(frontendBuildInfo.buildTime)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Server Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Server Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Node Version:</span>
                  <span className="font-mono text-sm">{systemInfo.server.nodeVersion}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Platform:</span>
                  <span className="font-mono text-sm">{systemInfo.server.platform}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Architecture:</span>
                  <span className="font-mono text-sm">{systemInfo.server.arch}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Uptime:</span>
                  <span className="font-mono text-sm">{systemInfo.server.uptimeFormatted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Process ID:</span>
                  <span className="font-mono text-sm">{systemInfo.server.pid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Timezone:</span>
                  <span className="font-mono text-sm">{systemInfo.docker.timezone}</span>
                </div>
              </div>
            </div>

            {/* Memory Usage */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Memory Usage</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">RSS:</span>
                  <span className="font-mono text-sm">
                    {(systemInfo.server.memory.rss / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Heap Used:</span>
                  <span className="font-mono text-sm">
                    {(systemInfo.server.memory.heapUsed / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Heap Total:</span>
                  <span className="font-mono text-sm">
                    {(systemInfo.server.memory.heapTotal / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">External:</span>
                  <span className="font-mono text-sm">
                    {(systemInfo.server.memory.external / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Environment Configuration */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Environment Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Environment:</span>
                  <span
                    className={`font-mono text-sm px-2 py-1 rounded ${
                      systemInfo.environment.nodeEnv === 'production'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {systemInfo.environment.nodeEnv}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Port:</span>
                  <span className="font-mono text-sm">{systemInfo.environment.port}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Server Host:</span>
                  <span className="font-mono text-sm">{systemInfo.environment.serverHost}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Frontend URL:</span>
                  <span className="font-mono text-sm">{systemInfo.environment.frontendUrl}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Backend URL:</span>
                  <span className="font-mono text-sm">{systemInfo.environment.backendUrl}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Containerized:</span>
                  <span
                    className={`font-mono text-sm px-2 py-1 rounded ${
                      systemInfo.docker.containerized
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {systemInfo.docker.containerized ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Features Status */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Features Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                {systemInfo.features.emailConfigured ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                )}
                <span className="text-gray-700">Email Service</span>
              </div>
              <div className="flex items-center space-x-2">
                {systemInfo.features.mapsApiKeySet ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                )}
                <span className="text-gray-700">Google Maps API</span>
              </div>
              <div className="flex items-center space-x-2">
                {systemInfo.features.importSampleData ? (
                  <Info className="w-5 h-5 text-blue-500" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
                <span className="text-gray-700">Sample Data Import</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Check Tab */}
      {activeTab === 'security' && securityCheck && (
        <div className="space-y-6">
          {/* Overall Status */}
          <div className={`border rounded-lg p-4 ${getStatusColor(securityCheck.overallStatus)}`}>
            <div className="flex items-center space-x-2">
              {getStatusIcon(securityCheck.overallStatus)}
              <span className="font-medium">
                Security Status: {securityCheck.overallStatus.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Security Checks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(securityCheck.checks).map(([key, check]) => (
              <div key={key} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </h3>
                  {getStatusIcon(check.status)}
                </div>
                <div className="space-y-2">
                  {Object.entries(check)
                    .filter(([k]) => k !== 'status')
                    .map(([subKey, value]) => (
                      <div key={subKey} className="flex justify-between text-sm">
                        <span className="text-gray-600 capitalize">
                          {subKey.replace(/([A-Z])/g, ' $1').trim()}:
                        </span>
                        <span className="font-mono">{renderValue(value)}</span>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>

          {/* Recommendations */}
          {securityCheck.recommendations.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-yellow-800 mb-4">Security Recommendations</h3>
              <ul className="space-y-2">
                {securityCheck.recommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span className="text-yellow-700">{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Environment Tab */}
      {activeTab === 'environment' && environment && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Environment Variables</h3>
              <p className="text-sm text-gray-600">
                Total variables: {environment.totalVariables} | Sensitive values are redacted for
                security
              </p>
            </div>
            <button
              onClick={() => setShowSensitive(!showSensitive)}
              className="btn btn-secondary btn-small flex items-center space-x-2"
            >
              {showSensitive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>{showSensitive ? 'Hide' : 'Show'} Sensitive</span>
            </button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {Object.entries(environment.environment).map(([key, value]) => (
                <div
                  key={key}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0"
                >
                  <span className="font-mono text-sm text-gray-600 flex-1">{key}</span>
                  <span className="font-mono text-sm text-gray-900 flex-1 ml-4">
                    {value === '[REDACTED]' && !showSensitive ? (
                      <span className="text-gray-400">[REDACTED]</span>
                    ) : (
                      value
                    )}
                  </span>
                  <button
                    onClick={() => copyToClipboard(`${key}=${value}`)}
                    className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemInfoPanel;
